<template>
  <ReportShell
    :title="title"
    :stats="stats"
    :selectedStats="filters.priceChange ? [filters.priceChange] : []"
    :statsHint="ready ? 'Clique em Aumentos, Reduções, Sem variação ou Primeira compra para filtrar os itens.' : ''"
    :exporting="exporting"
    :exportDisabled="!ready"
    @filter="reload"
    @clear="clearFilters"
    @export="exportExcel"
    @stat-click="toggleChange"
  >
    <template slot="filters">
      <a-col :xs="24" :md="16" :lg="7">
        <a-input-group compact class="scope-group">
          <a-select v-model="mode" class="scope-group__mode" @change="reload">
            <a-select-option value="request">Por cotação</a-select-option>
            <a-select-option value="period">Por período</a-select-option>
          </a-select>
          <a-select
            v-if="mode === 'request'"
            v-model="requestId"
            class="scope-group__value"
            show-search
            allow-clear
            placeholder="Número ou identificador da cotação"
            :filter-option="false"
            :dropdown-style="{ minWidth: '300px' }"
            :not-found-content="loadingRequests ? 'Buscando...' : 'Nenhuma cotação com pedido'"
            @search="searchRequests"
            @change="reload"
          >
            <a-select-option v-for="request in requests" :key="request.id" :value="request.id">
              {{ requestLabel(request) }}
            </a-select-option>
          </a-select>
          <ReportPeriodPicker v-else v-model="period" class="scope-group__value" @change="reload" />
        </a-input-group>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-select v-model="filters.priceChange" allow-clear placeholder="Variação de custo" @change="reload">
          <a-select-option v-for="option in CHANGES" :key="option.value" :value="option.value">
            {{ option.label }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-select
          v-model="filters.stores"
          mode="multiple"
          placeholder="Requisitante"
          class="fixed-height-select"
          :disabled="options.store_locked"
          :filter-option="filterOption"
          :max-tag-count="1"
          :dropdown-style="{ minWidth: '250px' }"
          @change="reload"
        >
          <a-select-option v-for="store in options.stores" :key="store.id" :value="store.id">
            {{ store.name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-select
          v-model="filters.supplier"
          show-search
          allow-clear
          placeholder="Fornecedor"
          :filter-option="filterOption"
          :dropdown-style="{ minWidth: '300px' }"
          @change="reload"
        >
          <a-select-option v-for="supplier in options.suppliers" :key="supplier.id" :value="supplier.id">
            {{ supplier.name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-select
          v-model="filters.category"
          show-search
          allow-clear
          placeholder="Categoria"
          :filter-option="filterOption"
          :dropdown-style="{ minWidth: '250px' }"
          @change="reload"
        >
          <a-select-option v-for="category in options.categories" :key="category.id" :value="category.id">
            {{ category.name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-input v-model="filters.search" placeholder="Produto" allow-clear @pressEnter="reload" />
      </a-col>
    </template>

    <p class="print-only price-report__print-title">Análise de Preço — {{ scopeLabel }}</p>

    <div v-if="!ready" class="price-report__hint">
      Escolha uma cotação para comparar o preço pago com o da última compra de cada produto.
    </div>

    <a-table
      v-else
      size="small"
      row-key="key"
      :columns="columns"
      :data-source="rows"
      :loading="loading"
      :pagination="pagination"
      :locale="{ emptyText: 'Nenhum item comprado neste recorte.' }"
      @change="tableChange"
    >
      <template slot="history" slot-scope="text, row">
        <a-button
          size="small"
          icon="history"
          class="not-print"
          title="Histórico de compras do produto"
          @click="openHistory(row)"
        />
      </template>
    </a-table>

    <ProductOrdersModal
      v-if="historyProduct"
      :product_id="historyProduct.id"
      :product_name="historyProduct.name"
      @close="historyProduct = null"
    />
  </ReportShell>
</template>

<script>
import ProductOrdersModal from "@/modals/products/orders";
import PriceVariationService from "@/services/v3/reports/price-variation.service";
import PurchaseReportService from "@/services/v3/reports/purchase-report.service";
import reportFormat from "./report-format.mixin";
import ReportPeriodPicker from "../components/ReportPeriodPicker.vue";
import ReportShell from "../components/ReportShell.vue";
import { currentMonth, periodParams } from "../components/report-period";

/**
 * Aba "Análise de Preço": preço pago x preço da última compra, por cotação
 * (como no legado) ou por período. Kyojin GET /clients/reports/price-variation.
 */
const CHANGES = [
  { value: "increase", label: "Aumentos", color: "#cf1322" },
  { value: "decrease", label: "Reduções", color: "#389e0d" },
  { value: "equal", label: "Sem variação", color: "#8c8c8c" },
  { value: "first", label: "Primeira compra", color: "#1890ff" },
];

const UP = "#cf1322";
const DOWN = "#389e0d";

const emptyTotals = () => ({ rows: 0, value: 0, variation: 0, increase: 0, decrease: 0, equal: 0, first: 0, request: null });

export default {
  name: "TabPriceVariation",
  components: { ProductOrdersModal, ReportPeriodPicker, ReportShell },
  mixins: [reportFormat],
  props: {
    title: { type: String, required: true },
    // Monitor de cotações abre esta aba direto numa cotação.
    cotacaoInput: { type: [Number, String], default: null },
  },
  data() {
    return {
      CHANGES,
      service: new PriceVariationService(),
      purchaseService: new PurchaseReportService(),
      mode: "request",
      period: currentMonth(),
      requestId: this.cotacaoInput ? Number(this.cotacaoInput) : undefined,
      requests: [],
      loadingRequests: false,
      searchTimer: null,
      filters: this.emptyFilters(),
      options: { stores: [], suppliers: [], categories: [], store_locked: false },
      rows: [],
      totals: emptyTotals(),
      meta: { current_page: 1, total: 0 },
      perPage: 30,
      sort: { key: null, dir: null },
      loading: false,
      exporting: false,
      historyProduct: null,
      requestSeq: 0,
    };
  },
  computed: {
    stats() {
      if (!this.ready) return [];
      const t = this.totals;
      const variation = t.variation || 0;
      return [
        {
          key: "value",
          title: "VALOR TOTAL",
          value: t.value || 0,
          precision: 2,
          prefix: "R$",
          footerText: this.scopeLabel,
          borderColor: "#1890ff",
        },
        {
          key: "variation",
          title: variation > 0 ? "AUMENTO X ÚLTIMA COMPRA" : "ECONOMIA X ÚLTIMA COMPRA",
          value: Math.abs(variation),
          precision: 2,
          prefix: "R$",
          valueColor: variation ? (variation > 0 ? UP : DOWN) : undefined,
          footerText: "diferença para o preço da última compra",
          borderColor: variation > 0 ? UP : DOWN,
        },
        { key: "rows", title: "ITENS COMPRADOS", value: t.rows || 0, suffix: "itens", footerText: "linhas no recorte", borderColor: "#722ed1" },
        ...CHANGES.map((option) => ({
          key: option.value,
          title: option.label.toUpperCase(),
          value: t[option.value] || 0,
          suffix: "itens",
          valueColor: option.color,
          footerText: `mostrar só: ${option.label.toLowerCase()}`,
          borderColor: option.color,
        })),
      ];
    },
    ready() {
      return this.mode === "period" || !!this.requestId;
    },
    scopeLabel() {
      if (this.mode === "request") {
        return this.totals.request ? this.requestLabel(this.totals.request) : "";
      }
      return `${this.formatDate(this.totals.from)} a ${this.formatDate(this.totals.to)}`;
    },
    pagination() {
      return {
        current: this.meta.current_page,
        pageSize: this.perPage,
        total: this.meta.total,
        showSizeChanger: true,
        pageSizeOptions: ["30", "100", "500"],
        showTotal: (total) => `${total} ${total === 1 ? "item" : "itens"}`,
      };
    },
    columns() {
      const sortable = (key) => ({
        key,
        sorter: true,
        sortOrder: this.sort.key === key ? (this.sort.dir === "asc" ? "ascend" : "descend") : false,
      });
      const money = (value) => (value === null || value === undefined ? "--" : this.brl(value));
      const h = this.$createElement;
      const signed = (value) =>
        value === null ? "--" : h("span", { class: this.variationClass(value) }, `${value > 0 ? "+" : ""}${this.brl(value)}`);

      return [
        { title: "Código", key: "ean", customRender: (t, row) => row.product.ean || "--" },
        { title: "Produto", ...sortable("name"), customRender: (t, row) => row.product.name || "--" },
        { title: "Fornecedor", key: "supplier", customRender: (t, row) => row.supplier.name || "--" },
        { title: "Marca", key: "brand", customRender: (t, row) => row.brand || "--" },
        { title: "Qtd.", align: "right", ...sortable("quantity"), customRender: (t, row) => this.num(row.quantity) },
        { title: "Prç. atual", align: "right", ...sortable("unit_price"), customRender: (t, row) => money(row.unit_price) },
        { title: "Prç. últ. compra", key: "last_price", align: "right", customRender: (t, row) => (row.last_price === null ? "Primeira compra" : money(row.last_price)) },
        { title: "Marca últ. compra", key: "last_brand", customRender: (t, row) => row.last_brand || "--" },
        { title: "Diferença", align: "right", ...sortable("difference"), customRender: (t, row) => signed(row.difference) },
        { title: "Variação", align: "right", ...sortable("variation"), customRender: (t, row) => signed(row.variation) },
        { title: "Valor total", align: "right", ...sortable("value"), customRender: (t, row) => this.brl(row.value) },
        // coluna em branco do legado, para anotar o novo preço no papel
        { title: "Novo preço", key: "new_price", className: "print-only-cell", customRender: () => "" },
        { title: "", key: "history", width: 48, scopedSlots: { customRender: "history" } },
      ];
    },
  },
  mounted() {
    this.purchaseService
      .options()
      .then(({ data }) => {
        this.options = data;
      })
      .catch(() => {});
    this.searchRequests(this.requestId ? String(this.requestId) : "");
    if (this.requestId) this.load(1);
  },
  methods: {
    emptyFilters() {
      return { stores: [], supplier: undefined, category: undefined, search: "", priceChange: undefined };
    },
    params(extra = {}) {
      const byRequest = this.mode === "request";
      return {
        request_id: byRequest ? this.requestId : undefined,
        ...(byRequest ? {} : periodParams(this.period)),
        store_id: this.filters.stores.length ? this.filters.stores : undefined,
        supplier_id: this.filters.supplier,
        category_id: this.filters.category,
        search: this.filters.search || undefined,
        price_change: this.filters.priceChange,
        sort: this.sort.key || undefined,
        dir: this.sort.key ? this.sort.dir : undefined,
        ...extra,
      };
    },
    searchRequests(search) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.loadingRequests = true;
        this.service
          .requests(search)
          .then(({ data }) => {
            this.requests = data.data;
          })
          .catch(() => {
            this.requests = [];
          })
          .finally(() => {
            this.loadingRequests = false;
          });
      }, 300);
    },
    requestLabel(request) {
      const parts = [`#${request.id}`];
      if (request.identifier) parts.push(request.identifier);
      if (request.created_at) parts.push(this.formatDate(request.created_at));
      return parts.join(" · ");
    },
    reload() {
      this.load(1);
    },
    load(page) {
      if (!this.ready) {
        this.rows = [];
        this.totals = emptyTotals();
        return;
      }
      const seq = ++this.requestSeq;
      this.loading = true;
      this.service
        .list(this.params({ page, per_page: this.perPage }))
        .then(({ data }) => {
          if (seq !== this.requestSeq) return;
          this.rows = data.data;
          this.totals = data.totals;
          this.meta = data.meta;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.rows = [];
          this.totals = emptyTotals();
          this.meta = { current_page: 1, total: 0 };
          this.notifyError(error, "Não foi possível carregar a análise de preço.");
        })
        .finally(() => {
          if (seq === this.requestSeq) this.loading = false;
        });
    },
    tableChange(pagination, _filters, sorter) {
      const key = sorter && sorter.order ? sorter.columnKey : null;
      const dir = sorter && sorter.order === "ascend" ? "asc" : "desc";
      const changed = key !== this.sort.key || (key && dir !== this.sort.dir) || pagination.pageSize !== this.perPage;
      this.sort = { key, dir: key ? dir : null };
      this.perPage = pagination.pageSize;
      this.load(changed ? 1 : pagination.current);
    },
    toggleChange(value) {
      // só os cards de variação filtram; os de total não fazem nada
      if (!CHANGES.some((option) => option.value === value)) return;
      this.filters.priceChange = this.filters.priceChange === value ? undefined : value;
      this.reload();
    },
    clearFilters() {
      this.filters = this.emptyFilters();
      this.sort = { key: null, dir: null };
      this.reload();
    },
    openHistory(row) {
      this.historyProduct = row.product;
    },
    exportExcel() {
      this.exporting = true;
      const name = this.mode === "request" ? `cotacao-${this.requestId}` : `${periodParams(this.period).from}-a-${periodParams(this.period).to}`;
      this.service
        .export(this.params())
        .then(({ data }) => this.downloadBlob(data, `analise-de-preco-${name}.xlsx`))
        .catch((error) => this.notifyError(error, "Não foi possível exportar a análise de preço."))
        .finally(() => {
          this.exporting = false;
        });
    },
    /** Positivo = pagou mais que na última compra (vermelho); negativo = economia (verde). */
    variationClass(value) {
      if (!value) return "";
      return value > 0 ? "price-report--up" : "price-report--down";
    },
  },
};
</script>

<style lang="scss" scoped>
.scope-group {
  display: flex !important;
  width: 100%;
}

.scope-group__mode {
  flex: 0 0 125px;
}

.scope-group__value {
  flex: 1;
  min-width: 0;
}

.price-report__hint {
  padding: 24px 16px;
  color: #605f5f;
}

.price-report--up {
  color: #cf1322;
}

.price-report--down {
  color: #389e0d;
}

.price-report__print-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.print-only {
  display: none;
}

::v-deep .print-only-cell {
  display: none;
}

@media print {
  .print-only {
    display: block;
  }

  ::v-deep .print-only-cell {
    display: table-cell;
    min-width: 90px;
  }
}
</style>
